import { ArrowBack, Visibility, VisibilityOff } from "@mui/icons-material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useFormik } from "formik";
import React, { FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { EmailIcon, PasswordIcon } from "src/grading/assets";
import InlineAlert from "src/grading/components/InlineAlert/InlineAlert";
import { inputError as InputError } from "src/grading/components/InputError/InputError";
import Loader from "src/grading/components/Loader/Loader";
import LoadingButton from "src/grading/components/LoadingButton/LoadingButton";
import EntryFrame, {
  EntryEyebrow,
  entryTitleSx,
} from "src/grading/layout/EntryFrame";
import { allowedRoles, checkRoles } from "src/grading/utils/permissionsHelpers";
import {
  Discovery,
  HostedUiError,
  startHostedUiSignIn,
} from "src/services/auth/hostedUi";
import {
  useDevLoginMutation,
  useLazyDiscoverSignInQuery,
  useLazyGetPermissionsQuery,
  useLazyGetUserQuery,
  useLoginMutation,
  useResetPasswordMutation,
} from "src/store/reducers/auth/authApiSlice";
import {
  logOut,
  selectCurrentUser,
  setCredentials,
  setPermissions,
  setUser,
} from "src/store/reducers/auth/authSlice";
import { DEV_LOGIN_ENABLED, DevSignIn } from "./DevSignIn";
import { emailStepSchema, loginStepSchema } from "./LoginValidation";

interface LoginProps {
  error: string;
  setError: React.Dispatch<React.SetStateAction<string>>;
}

/** Design entry inputs: 14 px radius, 52 px tall, 16/600 text. */
const entryFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    minHeight: 52,
    fontSize: 16,
    fontWeight: 600,
  },
} as const;

const STEP = {
  EMAIL: "EMAIL",
  SSO: "SSO",
  LOGIN: "LOGIN",
  FORGOT_PASSWORD: "FORGOT_PASSWORD",
  RESET_PASSWORD: "RESET_PASSWORD",
};

/** Where staff land after signing in. */
export const SIGNED_IN_PATH = "/grading/today";

interface ApiError {
  status?: number;
  message?: string;
  data?: { message?: string | string[] };
}

const asApiError = (err: unknown): ApiError =>
  typeof err === "object" && err !== null ? (err as ApiError) : {};

const apiMessage = (err: ApiError): string | undefined => {
  const message = err.data?.message;
  return Array.isArray(message) ? message.join(", ") : message;
};

type ProviderKind = "google" | "microsoft" | "school";

/** Maps the district's Cognito identity provider name to a known brand, when it names one. */
const providerKindOf = (identityProvider?: string): ProviderKind => {
  if (!identityProvider) return "school";
  if (/google/i.test(identityProvider)) return "google";
  if (/microsoft|azure|entra|office365/i.test(identityProvider))
    return "microsoft";
  return "school";
};

const Login = ({ error, setError }: LoginProps) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(STEP.EMAIL);
  const [showPassword, setShowPassword] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [districtName, setDistrictName] = useState<string | undefined>();
  const [discovery, setDiscovery] = useState<Discovery | undefined>();
  const history = useHistory();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const previousStep = useRef(step);

  // Move focus to the new step's heading so screen readers announce it; the first render keeps
  // the email field's autofocus.
  useEffect(() => {
    if (previousStep.current === step) return;
    previousStep.current = step;
    headingRef.current?.focus();
  }, [step]);

  const user = useSelector(selectCurrentUser);

  const [getUser] = useLazyGetUserQuery();
  const [getPermissions] = useLazyGetPermissionsQuery();
  const [discoverSignIn] = useLazyDiscoverSignInQuery();
  const [login, { isLoading: isLoginLoading }] = useLoginMutation();
  const [devLogin, { isLoading: isDevLoginLoading }] = useDevLoginMutation();
  const [forgotPassword, { isLoading: isResetPasswordLoading }] =
    useResetPasswordMutation();
  const dispatch = useDispatch();

  /** School email → sign-in mode. SSO districts get their provider button; others get the password form. */
  const handleContinue = async (email: string) => {
    setError("");
    setDiscovering(true);
    try {
      const result = await discoverSignIn(email.trim().toLowerCase()).unwrap();
      setDistrictName(result.districtName);
      if (result.mode === "sso") {
        setDiscovery(result);
        setStep(STEP.SSO);
        return;
      }
      setStep(STEP.LOGIN);
    } catch (err: unknown) {
      const e = asApiError(err);
      const message = apiMessage(e) ?? e.message;
      setError(
        e.status === 429
          ? t("auth.tooManyAttempts", {
              defaultValue: "Too many attempts. Wait a minute and try again.",
            })
          : message ||
              t("auth.discoverError", {
                defaultValue: "Could not check your school email.",
              }),
      );
    } finally {
      setDiscovering(false);
    }
  };

  const handleSso = async () => {
    if (!discovery) return;
    setError("");
    setRedirecting(true);
    try {
      await startHostedUiSignIn(discovery, values.email.trim().toLowerCase());
    } catch (err: unknown) {
      setError(
        err instanceof HostedUiError
          ? t(`auth.${err.code}`, { defaultValue: err.message })
          : asApiError(err).message ||
              t("auth.ssoUnreachable", {
                defaultValue: "Could not reach your school's sign-in.",
              }),
      );
      setRedirecting(false);
    }
  };

  const handleLogin = (email: string, password: string) =>
    completeSignIn(() => login({ email, password }).unwrap());

  /** Development builds only: sign in as a seeded profile, no password (docs/local-review.md). */
  const handleDevLogin = (email: string) =>
    completeSignIn(() => devLogin({ email }).unwrap());

  /**
   * Stores the tokens, then the permissions, then the profile. The profile goes last because it
   * lands in the store as soon as it loads, and a signed-in user is redirected away from this page,
   * so anything still to do after it would run from an unmounted page.
   */
  const completeSignIn = async (
    getTokens: () => Promise<{ accessToken: string; refreshToken: string }>,
  ) => {
    try {
      const loginResponse = await getTokens();
      dispatch(setCredentials({ ...loginResponse }));
      const permissionsData = await getPermissions().unwrap();
      if (!checkRoles(allowedRoles, permissionsData.roles)) {
        dispatch(logOut());
        setError(
          t("auth.staffOnly", {
            defaultValue:
              "This console is for school staff. Students and volunteers use the app.",
          }),
        );
        return;
      }
      dispatch(
        setPermissions({
          permissions: permissionsData.permissions,
          scopes: permissionsData.scopes,
          mfaRequired: permissionsData.mfaRequired,
          license: permissionsData.license,
        }),
      );
      const { profile } = await getUser().unwrap();
      dispatch(setUser({ user: profile }));
      history.push(
        permissionsData.mfaRequired ? "/grading/mfa" : SIGNED_IN_PATH,
      );
    } catch (err: unknown) {
      const e = asApiError(err);
      const message = apiMessage(e);
      setError(
        e.status && e.status < 500 && message
          ? message
          : t("auth.genericError", { defaultValue: "Something went wrong." }),
      );
      dispatch(logOut());
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      code: "",
      newPassword: "",
      validateNewPassword: "",
    },
    // The schema follows the step: requiring the password while only the email field is shown
    // made Continue a no-op (validation rejected a field the user could not see).
    validationSchema: () =>
      step === STEP.EMAIL ? emailStepSchema : loginStepSchema,
    onSubmit: async (submitted) => {
      if (step === STEP.EMAIL) {
        await handleContinue(submitted.email);
        return;
      }
      handleLogin(submitted.email, submitted.password);
    },
  });

  const { touched, values, errors, handleSubmit, handleChange } = formik;

  const handleForgotPassword = async (email: string) => {
    try {
      const response = await forgotPassword({ email }).unwrap();

      if (response.success) {
        setStep(STEP.RESET_PASSWORD);
      }
    } catch (err: unknown) {
      setError(
        apiMessage(asApiError(err)) ||
          t("auth.resetCodeError", {
            defaultValue: "Could not send the reset code. Try again.",
          }),
      );
    }
  };

  const handleResetPassword = async (code: string, newPassword: string) => {
    try {
      const response = await forgotPassword({
        code,
        password: newPassword,
        email: values.email,
      }).unwrap();

      if (response.success) {
        setStep(STEP.LOGIN);
      }
    } catch (err: unknown) {
      setError(
        apiMessage(asApiError(err)) ||
          t("auth.resetPasswordError", {
            defaultValue: "Could not change your password. Try again.",
          }),
      );
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError("");
    if (step === STEP.FORGOT_PASSWORD) {
      handleForgotPassword(values.email);
      return;
    }
    if (step === STEP.RESET_PASSWORD) {
      handleResetPassword(values.code, values.newPassword);
      return;
    }
    if (step === STEP.EMAIL || step === STEP.LOGIN) handleSubmit();
  };

  const staffSignIn = t("auth.staffSignIn", { defaultValue: "Staff sign-in" });
  const providerKind = providerKindOf(discovery?.identityProvider);
  const providerLabel =
    providerKind === "google"
      ? "Google"
      : providerKind === "microsoft"
        ? "Microsoft"
        : discovery?.identityProvider ||
          t("auth.singleSignOn", { defaultValue: "single sign-on" });

  const frame = (() => {
    switch (step) {
      case STEP.SSO:
        return {
          eyebrow: districtName || staffSignIn,
          title: t("auth.ssoTitle", {
            defaultValue: "Sign in with your school account",
          }),
        };
      case STEP.LOGIN:
        return {
          eyebrow: districtName || staffSignIn,
          title: t("auth.passwordTitle", {
            defaultValue: "Sign in with your password",
          }),
        };
      case STEP.FORGOT_PASSWORD:
        return {
          eyebrow: staffSignIn,
          title: t("auth.forgotPassword", {
            defaultValue: "Forgot your password?",
          }),
        };
      case STEP.RESET_PASSWORD:
        return {
          eyebrow: staffSignIn,
          title: t("auth.resetPassword", { defaultValue: "Password reset" }),
        };
      default:
        return {
          eyebrow: staffSignIn,
          title: t("auth.welcomeBack", {
            defaultValue: "Welcome back, coach.",
          }),
        };
    }
  })();

  const errorAlert =
    error.length > 0 ? (
      <InlineAlert severity="error">{error}</InlineAlert>
    ) : null;

  const backToSignIn = (
    <Button
      variant="text"
      startIcon={<ArrowBack />}
      onClick={() => {
        setError("");
        setStep(STEP.LOGIN);
      }}
      sx={{ alignSelf: "flex-start" }}
    >
      {t("auth.backToSignIn", { defaultValue: "Back to sign-in" })}
    </Button>
  );

  const helper = (text: React.ReactNode) => (
    <Typography
      sx={{ fontSize: 14.5, lineHeight: 1.45, color: "text.secondary" }}
    >
      {text}
    </Typography>
  );

  const emailField = (autoFocus: boolean) => (
    <TextField
      label={t("auth.schoolEmail", { defaultValue: "School email" })}
      name="email"
      type="email"
      autoComplete="username"
      value={values.email}
      onChange={handleChange("email")}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <EmailIcon />
          </InputAdornment>
        ),
      }}
      fullWidth
      autoFocus={autoFocus}
      sx={entryFieldSx}
      error={InputError("email", touched, errors).error}
      helperText={InputError("email", touched, errors).helperText}
    />
  );

  const renderStep = () => {
    if (user === undefined) return <Loader />;
    if (user !== null) return <div>Welcome, {user?.email}</div>;

    switch (step) {
      case STEP.EMAIL:
        return (
          <Stack spacing={2.5}>
            {helper(
              t("auth.emailHelper", {
                defaultValue:
                  "Enter your school email. If your district uses single sign-on, we'll send you straight there.",
              }),
            )}
            {emailField(true)}
            <LoadingButton
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              type="submit"
              sx={{ fontSize: 15, minHeight: 54 }}
              label={t("auth.continue", { defaultValue: "Continue" })}
              loading={discovering}
              disabled={discovering || !values.email.includes("@")}
            />
            {errorAlert}
            <Divider />
            <Box
              sx={(theme) => ({
                backgroundColor: theme.palette.surfaceAlt,
                borderRadius: "16px",
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1.5,
              })}
            >
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 0.375 }}
              >
                <Typography
                  component="h2"
                  sx={{ m: 0, fontSize: 14, fontWeight: 800 }}
                >
                  {t("auth.volunteerTitle", {
                    defaultValue: "Volunteering at a test day?",
                  })}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                  {t("auth.volunteerBody", {
                    defaultValue: "No account needed. Just the join code.",
                  })}
                </Typography>
              </Box>
              <Button
                component={RouterLink}
                to="/station"
                variant="contained"
                color="secondary"
                size="small"
                sx={{ whiteSpace: "nowrap", minHeight: 40 }}
              >
                {t("auth.joinWithCode", { defaultValue: "Join with a code" })}
              </Button>
            </Box>
            {DEV_LOGIN_ENABLED ? (
              <DevSignIn
                email={values.email}
                busy={isDevLoginLoading}
                onSignIn={handleDevLogin}
              />
            ) : null}
          </Stack>
        );
      case STEP.SSO:
        return (
          <Stack spacing={2.5}>
            {helper(
              t("auth.ssoHelper", {
                defaultValue: "{{email}} uses {{provider}}.",
                email: values.email.trim().toLowerCase(),
                provider: providerLabel,
              }),
            )}
            <LoadingButton
              variant="outlined"
              size="large"
              fullWidth
              type="button"
              onClick={handleSso}
              sx={(theme) => ({
                bgcolor: theme.palette.inputBg,
                fontSize: 15,
                fontWeight: 800,
                minHeight: 54,
              })}
              loading={redirecting}
              disabled={redirecting}
              label={
                providerKind === "google"
                  ? t("auth.continueWithGoogle", {
                      defaultValue: "Continue with Google",
                    })
                  : providerKind === "microsoft"
                    ? t("auth.continueWithMicrosoft", {
                        defaultValue: "Continue with Microsoft",
                      })
                    : t("auth.continueWithSchool", {
                        defaultValue: "Continue with your school account",
                      })
              }
            />
            {errorAlert}
            <Button
              variant="text"
              type="button"
              onClick={() => {
                setError("");
                setDiscovery(undefined);
                setStep(STEP.EMAIL);
              }}
              sx={{
                alignSelf: "flex-start",
                px: 0,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {`← ${t("auth.differentEmail", {
                defaultValue: "Use a different email",
              })}`}
            </Button>
          </Stack>
        );
      case STEP.LOGIN:
        return (
          <Stack spacing={2.5}>
            {helper(
              t("auth.passwordHelper", {
                defaultValue: "Enter the password for your staff account.",
              }),
            )}
            {emailField(false)}
            <TextField
              label={t("auth.password", { defaultValue: "Password" })}
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={values.password}
              onChange={handleChange("password")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      aria-label={
                        showPassword
                          ? t("auth.hidePassword", {
                              defaultValue: "Hide password",
                            })
                          : t("auth.showPassword", {
                              defaultValue: "Show password",
                            })
                      }
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
              error={InputError("password", touched, errors).error}
              helperText={InputError("password", touched, errors).helperText}
            />
            <Button
              variant="text"
              type="button"
              onClick={() => {
                setStep(STEP.FORGOT_PASSWORD);
                setError("");
              }}
              sx={{ alignSelf: "flex-end" }}
            >
              {t("auth.forgotPassword", {
                defaultValue: "Forgot your password?",
              })}
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              type="submit"
              sx={{ fontSize: 15, minHeight: 54 }}
              label={t("auth.signIn", { defaultValue: "Sign in" })}
              loading={isLoginLoading}
              disabled={
                !values.email ||
                !values.password ||
                values.email.includes("@") === false
              }
            />
            {errorAlert}
            <Button
              variant="text"
              type="button"
              onClick={() => {
                setError("");
                setStep(STEP.EMAIL);
              }}
              sx={{
                alignSelf: "flex-start",
                px: 0,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {`← ${t("auth.differentEmail", {
                defaultValue: "Use a different email",
              })}`}
            </Button>
          </Stack>
        );
      case STEP.FORGOT_PASSWORD:
        return (
          <Stack spacing={2.5}>
            {helper(
              t("auth.forgotHelper", {
                defaultValue:
                  "Enter your email address and we’ll send you a link to reset your password.",
              }),
            )}
            {emailField(false)}
            <LoadingButton
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              type="submit"
              sx={{ fontSize: 15, minHeight: 54 }}
              label={t("auth.sendResetCode", {
                defaultValue: "Reset my password",
              })}
              loading={isResetPasswordLoading}
              disabled={isResetPasswordLoading}
            />
            {errorAlert}
            {helper(
              t("auth.needHelp", { defaultValue: "Need help? Contact us." }),
            )}
            {backToSignIn}
          </Stack>
        );
      case STEP.RESET_PASSWORD:
        return (
          <Stack spacing={2.5}>
            {helper(
              t("auth.resetHelper", {
                defaultValue:
                  "We have sent a password reset code by email to {{email}}. Enter it below to reset your password.",
                email: values.email,
              }),
            )}
            <TextField
              label={t("auth.code", { defaultValue: "Code" })}
              name="code"
              type="text"
              autoComplete="one-time-code"
              value={values.code}
              onChange={handleChange("code")}
              fullWidth
              error={InputError("code", touched, errors).error}
              helperText={InputError("code", touched, errors).helperText}
            />
            <TextField
              name="newPassword"
              label={t("auth.newPassword", { defaultValue: "New password" })}
              type="password"
              autoComplete="new-password"
              value={values.newPassword}
              onChange={handleChange("newPassword")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <PasswordIcon />
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
            <TextField
              name="validateNewPassword"
              label={t("auth.confirmNewPassword", {
                defaultValue: "Enter new password again",
              })}
              type="password"
              autoComplete="new-password"
              value={values.validateNewPassword}
              onChange={handleChange("validateNewPassword")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <PasswordIcon />
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
            <LoadingButton
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              type="submit"
              sx={{ fontSize: 15, minHeight: 54 }}
              label={t("auth.changePassword", {
                defaultValue: "Change password",
              })}
              loading={isResetPasswordLoading}
              disabled={
                isResetPasswordLoading ||
                !values.code ||
                !values.newPassword ||
                values.newPassword !== values.validateNewPassword
              }
            />
            {errorAlert}
            {helper(
              t("auth.needHelp", { defaultValue: "Need help? Contact us." }),
            )}
            {backToSignIn}
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <EntryFrame>
      {/* Rendered here rather than through EntryFrame's `title` so it can take focus on step change. */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2.75 }}>
        <EntryEyebrow>{frame.eyebrow}</EntryEyebrow>
        <Typography
          ref={headingRef}
          variant="h1"
          tabIndex={-1}
          sx={{
            ...entryTitleSx,
            fontSize:
              step === STEP.EMAIL ? { xs: 28, sm: 32 } : { xs: 26, sm: 30 },
            "&:focus": { outline: "none" },
          }}
        >
          {frame.title}
        </Typography>
      </Box>
      <Box component="form" noValidate onSubmit={onSubmit}>
        {renderStep()}
      </Box>
    </EntryFrame>
  );
};

export default Login;
